import { Button, ButtonSize, ButtonVariant } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import React, { ChangeEvent, useRef, useState } from 'react';
import _ from 'underscore';

import { getTranslation, keys } from '../../../Resources/Translations';
import { EmailValidationResult } from '../../../Validation/EmailValidationResult.csharp';
import { EmailValidationStatus } from '../../../Validation/EmailValidationStatus.csharp';
import { postFor } from '../../ReactComponents/API';

interface EmailInputProps {
    errorMessage?: string;
    checkEmailUrl?: string;
    onEmailChange: (email: string) => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onInput?: () => void;
    setLoadingStatus?: (isLoading: boolean) => void;
    showEmailSuggestionMessage: boolean;
    disabled?: boolean;
}

export const EmailInput = (props: EmailInputProps) => {
    const [email, setEmail] = useState('');
    const [isEmailWarningVisible, setIsEmailWarningVisible] = useState<boolean>(false);
    const [emailSuggestion, setEmailSuggestion] = useState<string>('');

    const isEmailCheckInProgress = useRef<boolean>(false);
    const currentTimeout = useRef<NodeJS.Timeout | null>(null);

    const renderEmailSuggestionWarning = () => {
        if (!props.showEmailSuggestionMessage) {
            return null;
        }

        return emailSuggestion ? (
            <>
                {getTranslation(keys['newsletter.DidYouMean'])}
                <Button variant={ButtonVariant.Link} size={ButtonSize.Small} onClick={pickSuggestion}>
                    {emailSuggestion}
                </Button>
                ?
            </>
        ) : (
            getTranslation(keys['newsletter.AreYouSure'])
        );
    };

    const pickSuggestion = () => {
        setEmail(emailSuggestion);
        props.onEmailChange(emailSuggestion);
        setEmailSuggestion('');
        setIsEmailWarningVisible(false);
    };

    const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newEmail = event.target.value;
        setEmail(newEmail);

        props.setLoadingStatus?.(true);
        props.onInput && props.onInput();

        if (props.showEmailSuggestionMessage) {
            checkEmailValidityDebounced.current(newEmail);
        } else {
            props.onEmailChange(newEmail);
        }
    };

    const handleInputOnBlur = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value !== email) checkEmailValidity(event.target.value);
    };

    const checkEmailValidity = (newEmail: string) => {
        if (!newEmail || !props.checkEmailUrl) return props.onEmailChange(newEmail);

        if (!isEmailCheckInProgress.current) {
            isEmailCheckInProgress.current = true;

            postFor<EmailValidationResult>(props.checkEmailUrl, { emailAddress: newEmail })
                .then((result: EmailValidationResult) => {
                    if (result.suggestion) {
                        setIsEmailWarningVisible(props.showEmailSuggestionMessage);
                        setEmailSuggestion(result.suggestion);
                    } else if (result.status === EmailValidationStatus.Undeliverable) {
                        setIsEmailWarningVisible(props.showEmailSuggestionMessage);
                        setEmailSuggestion('');
                    } else {
                        setIsEmailWarningVisible(false);
                        setEmailSuggestion('');
                    }
                })

                .catch(() => {
                    setIsEmailWarningVisible(false);
                })
                .finally(() => {
                    props.onEmailChange(newEmail);
                    props.setLoadingStatus?.(false);
                    clearEmailValidationBusy();
                });
        }

        refreshValidationTimeout();
    };

    const checkEmailValidityDebounced = React.useRef(_.debounce(checkEmailValidity, 1000));

    const refreshValidationTimeout = () => {
        clearOldTimeout();
    };

    const clearEmailValidationBusy = () => {
        isEmailCheckInProgress.current = false;
        clearOldTimeout();
    };

    const clearOldTimeout = () => {
        if (currentTimeout.current) {
            clearTimeout(currentTimeout.current);
        }
    };

    const labelClassNames = classNames(
        'EmailInput__Label',
        isEmailWarningVisible && props.showEmailSuggestionMessage && 'EmailInput__Label--Warning',
        props.errorMessage && 'EmailInput__Label--Error'
    );

    return (
        <div className="EmailInput">
            <label className={labelClassNames}>
                <input
                    type="email"
                    placeholder={getTranslation(keys['newsletter.EmailPlaceholder'])}
                    value={email}
                    onChange={handleInputOnChange}
                    onKeyUp={props.onKeyUp}
                    onBlur={handleInputOnBlur}
                    className="EmailInput__Input"
                    disabled={props.disabled}
                />
            </label>

            {(props.errorMessage || isEmailWarningVisible) && (
                <span className={classNames('EmailInput__Message', props.errorMessage && 'EmailInput__Message--Error')}>
                    {props.errorMessage || renderEmailSuggestionWarning()}
                </span>
            )}
        </div>
    );
};
